<template>
  <v-container class="mt-0">
    <v-row>
      <v-col align-center class="pb-0">
        <div class="text-center">
          <span class="text-h6">Analysis for {{ recordName }}</span>
          <span class="font-weight-medium" v-if="analysisLoaded">
            &nbsp;&mdash;&nbsp;Similarity coefficent:
            {{ getSampleSizeText() }}
          </span>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="text-center body-2 pt-0 mb-2">
        <span>Factors: {{ getAnalysisCats() }}</span>
      </v-col>
    </v-row>
    <v-divider class=""></v-divider>
    <div v-if="analysisLoaded && results" class="mt-4 ml-4">
      <div v-for="cat in results.categories" :key="cat.name">
        <v-col class="pb-0 pt-3 body-1">
          <div>
            <v-btn
              small
              text
              @click.stop="toggleExpanded(cat.name)"
              class="body-2"
              :disabled="cat.values.length === 0"
            >
              <v-icon :class="{ open: expanded[cat.name] }">
                mdi-chevron-right
              </v-icon>
              <span class="results body-1">{{ cat.name }}:</span>
            </v-btn>
            <span class="font-weight-medium" v-if="cat.values.length !== 0">
              {{ displayValue(cat.values) }}
            </span>
            <span v-else>&lt;No values returned&gt;</span>
          </div>
          <div v-if="expanded[cat.name]" class="mt-2 graph-container">
            <div v-for="value in cat.values" :key="`${value.name}-graph`">
              <AnalysisGraphRow
                :recordCount="results.recordCount"
                :item="value"
              />
            </div>
          </div>
        </v-col>
      </div>
    </div>
    <div v-else class="text-h6 text-center mt-4 ml-4">
      <span>Analyzing...</span>
      <v-progress-circular
        class="ml-3"
        indeterminate
        :size="20"
        :width="1"
        color="secondary"
      ></v-progress-circular>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { setItem, getItem } from '../../../utils/storage';
import { strings } from 'vuex-store-builder';
import AnalysisGraphRow from './AnalysisGraphRow';
import moment from 'moment';

export default {
  name: 'AnalysisDisplay',
  data() {
    return {
      expanded: {},
      sampleSizeText: [
        '',
        'High',
        'Medium High',
        'Medium',
        'Medium Low',
        'Low',
      ],
    };
  },
  props: {
    isEmbedded: Boolean,
  },
  computed: {
    ...mapState({
      recordName: (state) => {
        if (state.records.currentlySelectedTab) {
          return state.records.currentlySelectedTab.recordName;
        } else {
          return 'Loading...';
        }
      },
      stateId: (state) => {
        if (state.records.currentlySelectedTab) {
          return state.records.currentlySelectedTab.typeSpecificPayload.stateId;
        } else {
          return '';
        }
      },
      analysisGroupName: (state) => {
        if (state.records.currentlySelectedTab) {
          return state.records.currentlySelectedTab.typeSpecificPayload.group;
        } else {
          return '';
        }
      },
      sampleSize: (state) => {
        if (state.records.currentlySelectedTab) {
          return state.records.currentlySelectedTab.typeSpecificPayload
            .sampleSize;
        } else {
          return 5;
        }
      },
      expandedStateKey: function() {
        return `${this.stateId}-${this.sampleSize}-expanded-state`;
      },
    }),
    ...mapState('cdgFetchCommonalities', {
      results: function(state) {
        return state.byId[this.stateId][this.sampleSize];
      },
      analysisLoaded: strings.loaded('fetchCommonalities'),
    }),
    ...mapGetters({
      analysisGroup: 'getAnalysisGroupByTab',
    }),
  },
  methods: {
    ...mapActions(['currentlySelectedTab']),
    formatJustTimestamp(timestamp) {
      return moment(timestamp).format('h:mm a');
    },
    formatJustDatestamp(timestamp) {
      return moment(timestamp).format('MMM Do, YYYY');
    },
    displayValue(item) {
      return item
        .map((entry) => {
          return `${entry.name.replace('_', ' ')}`;
        })
        .join(', ');
    },
    getPercent(val) {
      let newVal = val;
      if (Array.isArray(val)) {
        newVal = val.reduce((prevVal, currVal) => prevVal + currVal.count, 0);
      }
      const percent =
        Math.round((newVal / this.results.recordCount) * 1000) / 10;
      return percent;
    },
    toggleExpanded(key) {
      if (this.expanded[key]) {
        this.expanded[key] = !this.expanded[key];
      } else {
        this.$set(this.expanded, key, true);
      }
      setItem(this.expandedStateKey, this.expanded);
    },
    getAnalysisCats() {
      return this.analysisGroup[this.analysisGroupName].join(', ');
    },
    getSampleSizeText() {
      return this.sampleSizeText[this.sampleSize];
    },
  },
  filters: {
    displayFormat: function(value) {
      if (!value) return '';
      return value.replace('_', ' ');
    },
  },
  mounted() {
    const tabState = getItem(this.expandedStateKey);
    this.expanded = Object.assign({}, tabState);
  },
  components: {
    AnalysisGraphRow,
  },
};
</script>

<style lang="scss" scoped>
.open {
  transform: rotate(90deg);
  transition: rotate 2s;
}
.results {
  text-transform: none;
}
.graph-container {
  margin-left: 5rem;

  .graph {
    display: inline-block;

    &.left-side {
      text-align: right;
      margin-right: 0rem;
      padding-right: 1rem;
      border-right: 1px solid #000;
    }
    &.right-side {
      text-align: right;
      margin-left: 0rem;
      border: 1px solid #000;
      background-color: #4a90e2;
      height: 100%;
    }
  }
}
</style>
